import { useEffect, useMemo } from 'react';

import { useGetExerciseOptions } from '../redux/api/exerciseConfigAPI';
import {
  exerciseListNeedsOptionResolution,
  normalizeExerciseWithOptionRefs,
} from '../redux/api/exercises/map-available-exercises';
import { useGetAvailableExercises } from '../redux/api/exercisesAPI';
import { useLazyGetProgramItemExercises } from '../redux/api/programsAPI';
import { IExerciseOption } from '../types/interfaces/exercise-option';
import { orderBy, uniqBy } from '../utils/collections/array-utils';

export function useProgramItemExercises(params?: { programItemId?: string }) {
  const optionsState = useGetExerciseOptions();
  const resolvedExerciseOptions =
    optionsState.data ?? (optionsState.isError ? [] : undefined);
  const optionsBlocking =
    resolvedExerciseOptions === undefined &&
    !optionsState.isError &&
    (optionsState.isLoading || optionsState.isFetching);

  const {
    data: availableToUserExercises,
    isLoading: isLoadingAvailableToUserExercises,
    isFetching: isFetchingAvailableToUserExercises,
  } = useGetAvailableExercises();
  const [
    getProgramItemExercises,
    {
      data: programExercises,
      isLoading: isLoadingProgramExercises,
      isFetching: isFetchingProgramExercises,
    },
  ] = useLazyGetProgramItemExercises();

  useEffect(() => {
    if (params?.programItemId) {
      getProgramItemExercises(params.programItemId);
    }
  }, [params?.programItemId]);

  const blockedByOptions =
    optionsBlocking &&
    (exerciseListNeedsOptionResolution(availableToUserExercises) ||
      exerciseListNeedsOptionResolution(programExercises));

  const normalizedAvailableExercises = useMemo(() => {
    const list = availableToUserExercises ?? [];
    return list.map((exercise) =>
      normalizeExerciseWithOptionRefs(exercise, resolvedExerciseOptions ?? []),
    );
  }, [availableToUserExercises, resolvedExerciseOptions]);

  const normalizedProgramExercises = useMemo(() => {
    const list = programExercises ?? [];
    return list.map((exercise) =>
      normalizeExerciseWithOptionRefs(exercise, resolvedExerciseOptions ?? []),
    );
  }, [programExercises, resolvedExerciseOptions]);

  const exercises = useMemo(() => {
    if (
      isLoadingAvailableToUserExercises ||
      isFetchingAvailableToUserExercises ||
      isLoadingProgramExercises ||
      isFetchingProgramExercises ||
      blockedByOptions
    ) {
      return [];
    }

    let result = [];

    if (!programExercises) {
      result = [...normalizedAvailableExercises];
    } else {
      result = [...normalizedAvailableExercises, ...normalizedProgramExercises];
    }

    return orderBy(uniqBy(result, 'id'), 'title');
  }, [
    normalizedAvailableExercises,
    normalizedProgramExercises,
    programExercises,
    isLoadingAvailableToUserExercises,
    isFetchingAvailableToUserExercises,
    isLoadingProgramExercises,
    isFetchingProgramExercises,
    blockedByOptions,
  ]);

  const isLoading =
    isLoadingAvailableToUserExercises ||
    isFetchingAvailableToUserExercises ||
    isLoadingProgramExercises ||
    isFetchingProgramExercises ||
    blockedByOptions;

  return { exercises, isLoading };
}
